import React, { useEffect, useRef } from 'react';
import GarminLogo from '../brand/GarminLogo';

// Small presentational pieces shared by the Home card and Profile's "חיבורי אימונים" (2026-10-11).
// No state about any provider lives here — each provider row keeps its own existing logic.

/** Official asset when configured; otherwise a neutral initial tile (never a recreated brand mark). */
export function ProviderLogo({ source, size = 44 }) {
  // Garmin: the central brand element (official asset once approved, else a neutral "Garmin" text tile).
  // Decorative here: the provider name is always shown next to it.
  if (source.id === 'garmin') return <GarminLogo size={size} decorative />;
  const box = { width: size, height: size };
  if (source.logo) {
    return <img src={source.logo} alt="" style={box} className="rounded-control object-contain shrink-0" />;
  }
  return (
    <span aria-hidden="true" style={box}
          className="rounded-control bg-surface-2 border border-border text-text-muted font-bold flex items-center justify-center shrink-0 text-[15px]">
      {source.name.slice(0, 1)}
    </span>
  );
}

/** Provider name (LTR inline) + optional badge. */
export function ProviderName({ source, className = '' }) {
  return (
    <span className={`inline-flex items-center gap-2 min-w-0 ${className}`}>
      <bdi dir="ltr" className="font-bold text-[16px] text-text">{source.name}</bdi>
      {source.badge && (
        <span className="font-semibold text-[11px] text-accent bg-accent-soft rounded-badge px-1.5 py-0.5">{source.badge}</span>
      )}
    </span>
  );
}

const STATUS = {
  connected: { dot: 'bg-positive', text: 'text-positive', label: 'מחובר' },
  disconnected: { dot: 'border border-text-muted bg-transparent', text: 'text-text-muted', label: 'לא מחובר' },
  error: { dot: 'bg-quality', text: 'text-quality', label: 'נדרש טיפול' },
};

/** Dot + word (Home rows). */
export function StatusText({ status, label }) {
  const s = STATUS[status] || STATUS.disconnected;
  return (
    <span className={`inline-flex items-center gap-1.5 font-semibold text-[14px] ${s.text}`}>
      <span className={`w-2 h-2 rounded-full ${s.dot}`} aria-hidden="true" />
      {label || s.label}
    </span>
  );
}

/** Pill (Profile rows / sheet). */
export function StatusPill({ status, label }) {
  const s = STATUS[status] || STATUS.disconnected;
  const bg = status === 'connected' ? 'bg-positive-soft' : status === 'error' ? 'bg-quality-soft' : 'bg-surface-2';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-semibold text-[13px] ${bg} ${s.text}`}>
      <span className={`w-2 h-2 rounded-full ${s.dot}`} aria-hidden="true" />
      {label || s.label}
    </span>
  );
}

/** "⋯" icon button (44px touch target). */
export function MoreButton({ label, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-label={label}
            className="w-11 h-11 -me-2 rounded-control flex items-center justify-center text-text-muted hover:text-text hover:bg-surface-2 transition shrink-0">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <circle cx="10" cy="4" r="1.7" /><circle cx="10" cy="10" r="1.7" /><circle cx="10" cy="16" r="1.7" />
      </svg>
    </button>
  );
}

/** Bottom sheet in the app's modal pattern (fixed overlay). Esc / backdrop close; focus moves in. */
export function ManageSheet({ source, status, statusLabel, rows = [], note, onClose, onDisconnect, disconnectLabel,
                              busy = false, secondaryAction = null }) {
  const panel = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {                                  // once per opening: Esc closes, focus moves in
    const onKey = (e) => { if (e.key === 'Escape') closeRef.current(); };
    document.addEventListener('keydown', onKey);
    if (panel.current) panel.current.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70" dir="rtl"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={panel} tabIndex={-1} role="dialog" aria-modal="true" aria-label={`ניהול חיבור ${source.name}`}
           className="w-full sm:max-w-md bg-surface border border-border rounded-t-[20px] sm:rounded-card p-5 pb-6 outline-none max-h-[90vh] overflow-y-auto">
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-border sm:hidden" aria-hidden="true" />
        <div className="flex items-center gap-3">
          <ProviderLogo source={source} size={48} />
          <div className="min-w-0 flex-1">
            <ProviderName source={source} />
            <div className="text-[14px] text-text-muted">{source.description}</div>
          </div>
          <StatusPill status={status} label={statusLabel} />
        </div>
        {rows.length > 0 && (
          <dl className="mt-5 rounded-card bg-surface-2 border border-border divide-y divide-divider">
            {rows.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-3 px-4 py-3 text-[15px]">
                <dt className="text-text-muted">{k}</dt>
                <dd className="font-semibold text-text">{v}</dd>
              </div>
            ))}
          </dl>
        )}
        {note && <p className="mt-3 text-[13px] text-text-muted leading-relaxed">{note}</p>}
        <button type="button" onClick={onClose}
                className="mt-5 w-full h-12 rounded-control btn-primary text-[16px]">
          סגירה
        </button>
        {secondaryAction && (
          <button type="button" onClick={secondaryAction.onClick}
                  className="mt-2 w-full h-11 rounded-control text-text-body font-semibold text-[15px] hover:bg-surface-2">
            {secondaryAction.label}
          </button>
        )}
        {onDisconnect && (
          <button type="button" onClick={onDisconnect} disabled={busy}
                  className="mt-2 w-full h-11 rounded-control text-quality font-semibold text-[15px] disabled:opacity-50">
            {disconnectLabel}
          </button>
        )}
      </div>
    </div>
  );
}
